import React, { useState } from "react";
import AgentLayout from "../components/AgentLayout";
import NavButton from "../components/NavButton";
import DatePickerComponent from "../../client/components/DatePickerComponent";
import Bootstrap4DatePickerComponent from "../../client/components/Bootstrap4DatePickerComponent";
import axiosInstance from "../../../utils/axiosInstance";
import { useSelector } from "react-redux";

function toLocalString(d) {
  const pad = (x) => String(x).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

const DailyPlByUsers = () => {
  const today = new Date();
  const year = today.getFullYear();
  const monthIndex = today.getMonth();
  const dayOfMonth = today.getDate();
  const todayStart = new Date(year, monthIndex, dayOfMonth, 0, 0, 0, 0);
  const todayEnd = new Date(year, monthIndex, dayOfMonth, 23, 59, 59, 999);

  const [startDate, setStartDate] = useState(todayStart);
  const [endDate, setEndDate] = useState(todayEnd);
  const [showLoader, setShowLoader] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [users, setUsers] = useState([]);
  const [totals, setTotals] = useState({});
  const [grandTotal, setGrandTotal] = useState(0);
  const [pageLength, setPageLength] = useState(25);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [gamesDisplay, setGamesDisplay] = useState(false);
  const [gamesData, setGamesData] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedColumnLabel, setSelectedColumnLabel] = useState("");

  const userInfo = useSelector((state) => state.userStore);
  const username = userInfo?.data?.uname || "";

  const startDateSet = (date) => {
    setStartDate(date);
  };

  const endDateSet = (date) => {
    setEndDate(date);
  };

  const loadReport = async () => {
    setShowLoader(true);
    let frm = toLocalString(startDate);
    let til = toLocalString(endDate);

    try {
      const response = await axiosInstance.post("/master/daily-pl-by-users", {
        frm: frm,
        til: til
      });

      if (response.status === 200 && response.data?.R) {
        setUsers(response.data.users || []);
        setTotals(response.data.totals || {});
        setGrandTotal(response.data.grandTotal || 0);
        setDataLoaded(true);
        setPage(1);
        setSearchText("");
        setGamesDisplay(false);
        setGamesData([]);
        setSelectedUser(null);
        setSelectedColumnLabel("");
      }
    } catch (error) {
      console.log("Error loading report:", error);
    }

    setShowLoader(false);
  };

  const formatAmount = (value) => {
    const num = Math.round(Number(value) || 0);
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  };

  const getAmountColor = (value) => {
    const num = Number(value) || 0;
    if (num < 0) return "red";
    if (num > 0) return "#00b181";
    return "#212529";
  };

  const loadUserGames = async (user, columnKey, columnLabel) => {
    setShowLoader(true);
    setSelectedUser(user);
    setSelectedColumnLabel(columnLabel || "All Games");

    try {
      const response = await axiosInstance.post(
        "/master/daily-pl-by-users/games",
        {
          user_id: user.user_id,
          column_key: columnKey || "total",
          frm: toLocalString(startDate),
          til: toLocalString(endDate)
        }
      );

      if (response.status === 200 && response.data?.R) {
        setGamesData(response.data.games || []);
        setGamesDisplay(true);
      }
    } catch (error) {
      console.log("Error loading user games:", error);
      setGamesData([]);
      setGamesDisplay(true);
    }

    setShowLoader(false);
  };

  const openRoundPlayersPopup = (game) => {
    if (!game?.trans_id) return;

    const popup = window.open(
      `/daily-pl-round-players/${game.trans_id}`,
      "",
      "width=700,height=500"
    );
    if (popup) {
      popup.document.title = "BPEXCH";
    }
  };

  const renderClickableGameCell = (game, content, fieldLabel, amountValue) => {
    if (!game?.trans_id) {
      return content;
    }

    const linkColor =
      fieldLabel === "amount" ? getAmountColor(amountValue || 0) : "#00b181";

    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => openRoundPlayersPopup(game)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openRoundPlayersPopup(game);
          }
        }}
        style={{
          color: linkColor,
          cursor: "pointer",
          textDecoration: "underline",
          fontWeight: fieldLabel === "amount" ? 500 : 400
        }}
        title="Click to view all players in this round"
      >
        {content}
      </span>
    );
  };

  const renderClickableAmount = (user, value, columnKey, columnLabel) => {
    const num = Number(value) || 0;
    const color = getAmountColor(num);
    const formatted = formatAmount(num);

    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => loadUserGames(user, columnKey, columnLabel)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            loadUserGames(user, columnKey, columnLabel);
          }
        }}
        style={{
          color: color,
          fontWeight: 500,
          cursor: "pointer",
          textDecoration: "underline"
        }}
        title="Click to view games"
      >
        {formatted}
      </span>
    );
  };

  const gameColumns = [
    { key: "soccer", label: "Soccer" },
    { key: "tennis", label: "Tennis" },
    { key: "cricket", label: "Cricket" },
    { key: "horseRace", label: "Horse Race" },
    { key: "greyhound", label: "Greyhound" },
    { key: "bf_hold", label: "Hold'em" },
    { key: "bf_turbo_hold", label: "Turbo Hold'em" },
    { key: "bf_blackjack", label: "BlackJack" },
    { key: "bf_turbo_blackjack", label: "Turbo BlackJack" },
    { key: "bf_baccarat", label: "Baccarat" },
    { key: "bf_turbo_baccarat", label: "Turbo Baccarat" },
    { key: "bf_hilo", label: "Hi Lo" },
    { key: "bf_turbo_hilo", label: "Turbo HiLo" },
    { key: "bf_omaha", label: "Omaha Hi" },
    { key: "bf_derby", label: "Derby Race" },
    { key: "bf_turbo_derby", label: "Derby Turbo" },
    { key: "tp_chicken_road", label: "Chicken Road" },
    { key: "tp_aviator", label: "Aviator" },
    { key: "tp_aviatorx", label: "AviatorX" },
    { key: "tp_blastoff", label: "BlastOff" },
    { key: "tp_cricketx", label: "CricketX" },
    { key: "tp_dream_catcher", label: "Dream Catcher" },
    { key: "tp_teenpatti_t20", label: "TeenPatti T20" },
    { key: "tp_muflis", label: "Muflis" },
    { key: "tp_roulette", label: "Roulette" },
    { key: "tp_rps", label: "RPS" },
    { key: "tp_poker", label: "Poker" },
    { key: "tp_sicbo", label: "Sic-Bo" },
    { key: "tp_andar_bahar", label: "AndarBahar" },
    { key: "tp_hilo", label: "HiLo" },
    { key: "tp_dragon_tiger", label: "Dragon Tiger" },
    { key: "tp_lucky7", label: "Lucky 7" },
    { key: "tp_aaa", label: "AAA" },
    { key: "tp_super_over_t20", label: "Super Over T20" },
    { key: "tp_32_cards", label: "32 Cards" },
    { key: "tp_bollywood", label: "Bollywood" },
    { key: "tp_race17", label: "Race 17" },
    { key: "tp_race20", label: "Race 20" },
    { key: "tp_high_card", label: "High Card" },
    { key: "tp_casino_war", label: "Casino War" },
    { key: "tp_lucky7_updown", label: "7UpDown" },
    { key: "tp_center_card", label: "Center Card" },
    { key: "tp_trio", label: "TRIO" },
    { key: "tp_trap", label: "TRAP" },
    { key: "tp_one_card_2020", label: "One Card 2020" },
    { key: "tp_one_card_meter", label: "One Card Meter" },
    { key: "tp_queen_race", label: "Queen Race" },
    { key: "tp_dus_ka_dum", label: "Dus Ka Dum" },
    { key: "tp_side_bet_city", label: "Side Bet City" },
    { key: "tp_lottery", label: "Lottery" },
    { key: "tp_head_tail", label: "HEAD TAIL" },
    { key: "tp_super_over_1day", label: "Super Over 1Day" },
    { key: "tp_fortune_gems", label: "Fortune Gems" },
    { key: "tp_color_game", label: "COLOR GAME" },
    { key: "tp_roll_dice", label: "ROLL DICE" },
    { key: "gx_lucky_jet", label: "Lucky Jet" },
    { key: "gx_lucky7", label: "7-Up-Down" },
    { key: "gx_tp20", label: "TP20" },
    { key: "gx_hilo", label: "HiLo GX" },
    { key: "gx_andar_bahar", label: "Andar Bahar GX" },
    { key: "gx_dragon_tiger", label: "Dragon Tiger GX" },
    { key: "total", label: "Total" }
  ];

  // Filter users by search
  let filteredUsers = users;
  if (searchText && searchText.trim() !== "") {
    const searchLower = searchText.trim().toLowerCase();
    filteredUsers = [];
    for (let i = 0; i < users.length; i++) {
      const user = users[i];
      const userName = (user.uname || "").toLowerCase();
      const userLabel = `${user.uname || ""} (${
        user.currency || "INR"
      })`.toLowerCase();
      if (userName.includes(searchLower) || userLabel.includes(searchLower)) {
        filteredUsers.push(user);
      }
    }
  }

  // Calculate pagination
  const totalRecords = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageLength));
  const currentPage = Math.min(page, totalPages);
  const rangeStart =
    totalRecords === 0 ? 0 : (currentPage - 1) * pageLength + 1;
  const rangeEnd = Math.min(currentPage * pageLength, totalRecords);

  // Get current page rows
  const pageRows = [];
  for (let i = rangeStart - 1; i < rangeEnd; i++) {
    if (filteredUsers[i]) {
      pageRows.push(filteredUsers[i]);
    }
  }

  const handlePrevious = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  return (
    <AgentLayout>
      <style>{`
        .react-datepicker-popper { left: unset; }
        .dpl-summary-wrap {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 14px;
        }
        .dpl-summary-box {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          border: 1px solid #ced4da;
          background: #f8f9fa;
          border-radius: 3px;
          padding: 4px 8px;
          font-size: 12px;
          line-height: 1.2;
          white-space: nowrap;
        }
        .dpl-summary-total {
          background: #fff3cd;
          border-color: #ffc107;
          font-weight: 600;
        }
        .dpl-section-header {
          width: 100%;
          font-weight: 600;
          font-size: 13px;
          color: #495057;
          padding: 6px 0 4px 0;
          border-bottom: 2px solid #dee2e6;
          margin-top: 8px;
          margin-bottom: 4px;
        }
        .dpl-section-header:first-child {
          margin-top: 0;
        }
        .dpl-box-icon {
          color: #6c757d;
          font-size: 11px;
        }
        .dpl-summary-label { color: #343a40; }
        .dpl-summary-sep { color: #6c757d; }
        .dpl-summary-value { font-weight: 600; }
        .dpl-table-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          gap: 12px;
          flex-wrap: wrap;
        }
        .dpl-table-controls label {
          margin: 0;
          font-weight: 500;
        }
        .dpl-matrix-table th,
        .dpl-matrix-table td {
          white-space: nowrap;
          vertical-align: middle !important;
          font-size: 12px;
        }
        .dpl-matrix-table thead th {
          background: #e9ecef;
        }
        .dpl-col-icon {
          margin-right: 4px;
          color: #6c757d;
        }
        .dpl-table-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 10px;
          flex-wrap: wrap;
          gap: 8px;
        }
        .dpl-games-section {
          margin-top: 20px;
        }
        .dpl-games-table th,
        .dpl-games-table td {
          white-space: nowrap;
          vertical-align: middle !important;
          font-size: 12px;
        }
        .dpl-games-table thead th {
          background: #e9ecef;
        }
        .loaderAgent {
          position: fixed;
          z-index: 99;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: #fff;
          opacity: .6;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        @media (max-width: 640px) {
          #ReportFilterForm .row > .col-12.col-md-5 {
            flex: 0 0 100%;
            max-width: 100%;
            margin-bottom: 8px;
          }
          #ReportFilterForm .row > strong { display: none; }
          #ReportFilterForm .row > .editsbmtbtn {
            width: 100%;
            display: flex;
            justify-content: flex-end;
            padding-right: 20px;
          }
        }
      `}</style>

      <div className="container-fluid">
        <div
          className="loaderAgent"
          style={{ display: showLoader ? "" : "none" }}
        >
          <img src="/assets/images/loadinggif.gif" alt="" />
        </div>

        <div className="bets-section-wrapper">
          <div>
            <NavButton />
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <i className="fa fa-filter"></i>
                  <strong>Report Filter</strong>
                </div>
                <div
                  className="card-body"
                  style={{ padding: "0.8rem 1.25rem" }}
                >
                  <form
                    id="ReportFilterForm"
                    className="form-inline"
                    method="get"
                  >
                    <div className="row" style={{ textAlignLast: "justify" }}>
                      <div className="col-12 col-md-5">
                        <Bootstrap4DatePickerComponent
                          pickerId="ReportFrom"
                          inputId="DisplayFrom"
                          defaultDate={startDate}
                          setSelectedDate={startDateSet}
                        />
                        {/*<DatePickerComponent
                          defaultDate={startDate}
                          setSelectedDate={setStartDate}
      />*/}
                      </div>
                      <strong style={{ margin: "auto" }}>&nbsp;-&nbsp;</strong>
                      <div className="col-12 col-md-5">
                      <Bootstrap4DatePickerComponent
                            pickerId="ReportTo"
                            inputId="DisplayTo"
                            defaultDate={endDate}
                            setSelectedDate={endDateSet}
                          />

                        {/*<DatePickerComponent
                          defaultDate={endDate}
                          setSelectedDate={setEndDate}
      />*/}
                      </div>
                      <div className="form-group editsbmtbtn">
                        <button
                          onClick={() => loadReport()}
                          className="btn btn-success"
                          style={{ background: "#02b181" }}
                          type="button"
                        >
                          <strong>Submit</strong>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="bets-wrapper"
          style={{ marginTop: "20px", display: dataLoaded ? "" : "none" }}
        >
          <div className="card">
            <div className="card-header">
              <i className="fa fa-align-justify"></i>
              <span style={{ fontWeight: 600 }}> {username}</span>
              <span style={{ marginLeft: 8 }}>
                Daily PL by Bettors (Players)
              </span>
            </div>
            <div className="card-body">
              <div className="dpl-summary-wrap">
                {/* Sports Section */}
                <div className="dpl-section-header">SPORTS</div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Soccer</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.soccer || 0) }}
                  >
                    {formatAmount(totals.soccer || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Tennis</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tennis || 0) }}
                  >
                    {formatAmount(totals.tennis || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Cricket</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.cricket || 0) }}
                  >
                    {formatAmount(totals.cricket || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Horse Race</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.horseRace || 0) }}
                  >
                    {formatAmount(totals.horseRace || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Greyhound</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.greyhound || 0) }}
                  >
                    {formatAmount(totals.greyhound || 0)}
                  </span>
                </div>

                {/* Betfair Studio Section */}
                <div className="dpl-section-header">BETFAIR STUDIO</div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Hold'em</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.bf_hold || 0) }}
                  >
                    {formatAmount(totals.bf_hold || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Turbo Hold'em</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.bf_turbo_hold || 0) }}
                  >
                    {formatAmount(totals.bf_turbo_hold || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">BlackJack</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.bf_blackjack || 0) }}
                  >
                    {formatAmount(totals.bf_blackjack || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Turbo BlackJack</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.bf_turbo_blackjack || 0)
                    }}
                  >
                    {formatAmount(totals.bf_turbo_blackjack || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Baccarat</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.bf_baccarat || 0) }}
                  >
                    {formatAmount(totals.bf_baccarat || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Turbo Baccarat</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.bf_turbo_baccarat || 0)
                    }}
                  >
                    {formatAmount(totals.bf_turbo_baccarat || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Hi Lo</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.bf_hilo || 0) }}
                  >
                    {formatAmount(totals.bf_hilo || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Turbo HiLo</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.bf_turbo_hilo || 0) }}
                  >
                    {formatAmount(totals.bf_turbo_hilo || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Omaha Hi</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.bf_omaha || 0) }}
                  >
                    {formatAmount(totals.bf_omaha || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Derby Race</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.bf_derby || 0) }}
                  >
                    {formatAmount(totals.bf_derby || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Derby Turbo</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.bf_turbo_derby || 0)
                    }}
                  >
                    {formatAmount(totals.bf_turbo_derby || 0)}
                  </span>
                </div>

                {/* TP Studio Section */}
                <div className="dpl-section-header">TEENPATTI STUDIO</div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Chicken Road</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_chicken_road || 0)
                    }}
                  >
                    {formatAmount(totals.tp_chicken_road || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Aviator</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_aviator || 0) }}
                  >
                    {formatAmount(totals.tp_aviator || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">AviatorX</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_aviatorx || 0) }}
                  >
                    {formatAmount(totals.tp_aviatorx || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">BlastOff</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_blastoff || 0) }}
                  >
                    {formatAmount(totals.tp_blastoff || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">CricketX</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_cricketx || 0) }}
                  >
                    {formatAmount(totals.tp_cricketx || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Dream Catcher</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_dream_catcher || 0)
                    }}
                  >
                    {formatAmount(totals.tp_dream_catcher || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">TeenPatti T20</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_teenpatti_t20 || 0)
                    }}
                  >
                    {formatAmount(totals.tp_teenpatti_t20 || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Muflis</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_muflis || 0) }}
                  >
                    {formatAmount(totals.tp_muflis || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Roulette</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_roulette || 0) }}
                  >
                    {formatAmount(totals.tp_roulette || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">RPS</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_rps || 0) }}
                  >
                    {formatAmount(totals.tp_rps || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Poker</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_poker || 0) }}
                  >
                    {formatAmount(totals.tp_poker || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Sic-Bo</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_sicbo || 0) }}
                  >
                    {formatAmount(totals.tp_sicbo || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">AndarBahar</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_andar_bahar || 0)
                    }}
                  >
                    {formatAmount(totals.tp_andar_bahar || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">HiLo</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_hilo || 0) }}
                  >
                    {formatAmount(totals.tp_hilo || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Dragon Tiger</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_dragon_tiger || 0)
                    }}
                  >
                    {formatAmount(totals.tp_dragon_tiger || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Lucky 7</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_lucky7 || 0) }}
                  >
                    {formatAmount(totals.tp_lucky7 || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">AAA</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_aaa || 0) }}
                  >
                    {formatAmount(totals.tp_aaa || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Super Over T20</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_super_over_t20 || 0)
                    }}
                  >
                    {formatAmount(totals.tp_super_over_t20 || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">32 Cards</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_32_cards || 0) }}
                  >
                    {formatAmount(totals.tp_32_cards || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Bollywood</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_bollywood || 0) }}
                  >
                    {formatAmount(totals.tp_bollywood || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Race 17</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_race17 || 0) }}
                  >
                    {formatAmount(totals.tp_race17 || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Race 20</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_race20 || 0) }}
                  >
                    {formatAmount(totals.tp_race20 || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">High Card</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_high_card || 0) }}
                  >
                    {formatAmount(totals.tp_high_card || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Casino War</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_casino_war || 0) }}
                  >
                    {formatAmount(totals.tp_casino_war || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">7UpDown</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_lucky7_updown || 0)
                    }}
                  >
                    {formatAmount(totals.tp_lucky7_updown || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Center Card</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_center_card || 0)
                    }}
                  >
                    {formatAmount(totals.tp_center_card || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">TRIO</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_trio || 0) }}
                  >
                    {formatAmount(totals.tp_trio || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">TRAP</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_trap || 0) }}
                  >
                    {formatAmount(totals.tp_trap || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">One Card 2020</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_one_card_2020 || 0)
                    }}
                  >
                    {formatAmount(totals.tp_one_card_2020 || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">One Card Meter</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_one_card_meter || 0)
                    }}
                  >
                    {formatAmount(totals.tp_one_card_meter || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Queen Race</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_queen_race || 0) }}
                  >
                    {formatAmount(totals.tp_queen_race || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Dus Ka Dum</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_dus_ka_dum || 0) }}
                  >
                    {formatAmount(totals.tp_dus_ka_dum || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Side Bet City</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_side_bet_city || 0)
                    }}
                  >
                    {formatAmount(totals.tp_side_bet_city || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Lottery</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_lottery || 0) }}
                  >
                    {formatAmount(totals.tp_lottery || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">HEAD TAIL</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_head_tail || 0) }}
                  >
                    {formatAmount(totals.tp_head_tail || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Super Over 1Day</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_super_over_1day || 0)
                    }}
                  >
                    {formatAmount(totals.tp_super_over_1day || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Fortune Gems</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.tp_fortune_gems || 0)
                    }}
                  >
                    {formatAmount(totals.tp_fortune_gems || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">COLOR GAME</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_color_game || 0) }}
                  >
                    {formatAmount(totals.tp_color_game || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">ROLL DICE</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.tp_roll_dice || 0) }}
                  >
                    {formatAmount(totals.tp_roll_dice || 0)}
                  </span>
                </div>

                {/* Galaxy Casino Section */}
                <div className="dpl-section-header">GALAXY CASINO</div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Lucky Jet</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.gx_lucky_jet || 0) }}
                  >
                    {formatAmount(totals.gx_lucky_jet || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">7-Up-Down</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.gx_lucky7 || 0) }}
                  >
                    {formatAmount(totals.gx_lucky7 || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">TeenPatti T20</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.gx_tp20 || 0) }}
                  >
                    {formatAmount(totals.gx_tp20 || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">HiLo</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(totals.gx_hilo || 0) }}
                  >
                    {formatAmount(totals.gx_hilo || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Andar Bahar</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.gx_andar_bahar || 0)
                    }}
                  >
                    {formatAmount(totals.gx_andar_bahar || 0)}
                  </span>
                </div>
                <div className="dpl-summary-box">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Dragon Tiger</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{
                      color: getAmountColor(totals.gx_dragon_tiger || 0)
                    }}
                  >
                    {formatAmount(totals.gx_dragon_tiger || 0)}
                  </span>
                </div>

                <div className="dpl-summary-box dpl-summary-total">
                  <i className="fa fa-cube dpl-box-icon" />
                  <span className="dpl-summary-label">Total</span>
                  <span className="dpl-summary-sep">:</span>
                  <span
                    className="dpl-summary-value"
                    style={{ color: getAmountColor(grandTotal) }}
                  >
                    {formatAmount(grandTotal)}
                  </span>
                </div>
              </div>

              <div className="dpl-table-controls">
                <div>
                  <label>
                    Show{" "}
                    <select
                      className="form-control form-control-sm d-inline-block"
                      style={{ width: "auto", display: "inline-block" }}
                      value={pageLength}
                      onChange={(e) => {
                        setPageLength(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>{" "}
                    records per page
                  </label>
                </div>
                <div>
                  <label>
                    Search:{" "}
                    <input
                      type="search"
                      className="form-control form-control-sm d-inline-block"
                      style={{ width: 180, display: "inline-block" }}
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setPage(1);
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className="table-responsive">
                <table
                  className="table table-bordered table-sm dpl-matrix-table"
                  style={{ width: "100%" }}
                >
                  <thead>
                    <tr>
                      <th>Bettor ID</th>
                      {gameColumns.map((col) => (
                        <th key={col.key}>
                          <i className="fa fa-cube dpl-col-icon" />
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={gameColumns.length + 1}
                          className="text-center text-muted"
                        >
                          No records found for the selected period.
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((user) => (
                        <tr key={user.user_id}>
                          <td>
                            {user.uname} ({user.currency || "INR"})
                          </td>
                          {gameColumns.map((col) => (
                            <td key={col.key}>
                              {renderClickableAmount(
                                user,
                                user[col.key] || 0,
                                col.key,
                                col.label
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="dpl-table-footer">
                <div>
                  Showing {rangeStart} to {rangeEnd} of {totalRecords} entries
                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage <= 1}
                    onClick={handlePrevious}
                    style={{ marginRight: 6 }}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={currentPage >= totalPages}
                    onClick={handleNext}
                  >
                    Next
                  </button>
                </div>
              </div>

              {gamesDisplay && selectedUser && (
                <div className="dpl-games-section">
                  <div className="card">
                    <div className="card-header">
                      <i className="fa fa-align-justify"></i>
                      <span style={{ fontWeight: 600, marginLeft: 8 }}>
                        {selectedUser.uname} ({selectedUser.currency || "INR"})
                      </span>
                      <span style={{ marginLeft: 8 }}>
                        - {selectedColumnLabel} - Games Report
                      </span>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm dpl-games-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Game</th>
                              <th>Game Type</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {gamesData.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="text-center text-muted"
                                >
                                  No games found for the selected period.
                                </td>
                              </tr>
                            ) : (
                              gamesData.map((game) => (
                                <tr key={game.id}>
                                  <td>
                                    {renderClickableGameCell(
                                      game,
                                      game.created_date,
                                      "date"
                                    )}
                                  </td>
                                  <td>
                                    {renderClickableGameCell(
                                      game,
                                      game.game_name || "-",
                                      "game"
                                    )}
                                  </td>
                                  <td>
                                    {renderClickableGameCell(
                                      game,
                                      game.game_type_name || "-",
                                      "type"
                                    )}
                                  </td>
                                  <td>
                                    {renderClickableGameCell(
                                      game,
                                      formatAmount(game.amount || 0),
                                      "amount",
                                      game.amount || 0
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AgentLayout>
  );
};

export default DailyPlByUsers;
